import { lazy, Suspense, useState } from "react"

import { useConsumoAgrupado, useConsumoDiario, useResumen } from "@/api/queries"
import type { Agrupacion } from "@/api/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
// recharts pesa más que el resto de la aplicación junta y solo la usa esta tarjeta.
// Cargándola aparte, el login y las demás páginas no la descargan.
const CostChart = lazy(() =>
  import("@/features/usage/cost-chart").then((m) => ({ default: m.CostChart })),
)
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { formatearCoste, formatearNumero, formatearPorcentaje } from "@/shared/format"

const AGRUPACIONES: { valor: Agrupacion; etiqueta: string }[] = [
  { valor: "feature", etiqueta: "Funcionalidad" },
  { valor: "alias", etiqueta: "Alias" },
  { valor: "provider", etiqueta: "Proveedor" },
  { valor: "model", etiqueta: "Modelo" },
]

function Metrica({
  titulo,
  valor,
  pista,
  alerta = false,
}: {
  titulo: string
  valor: string
  pista?: string
  alerta?: boolean
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{titulo}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={alerta ? "text-2xl font-semibold text-destructive" : "text-2xl font-semibold"}>
          {valor}
        </div>
        {pista !== undefined && <p className="mt-1 text-xs text-muted-foreground">{pista}</p>}
      </CardContent>
    </Card>
  )
}

export function UsagePage() {
  const [dias] = useState(30)
  const [por, setPor] = useState<Agrupacion>("feature")

  const resumen = useResumen(dias)
  const diario = useConsumoDiario(dias)
  const agrupado = useConsumoAgrupado(por, dias)

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Consumo · últimos {dias} días</h1>

      {resumen.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : resumen.isError ? (
        <p className="text-sm text-destructive">No se pudo cargar el consumo.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Metrica titulo="Coste" valor={formatearCoste(resumen.data.cost_micros)} />
          <Metrica titulo="Peticiones" valor={formatearNumero(resumen.data.peticiones)} />
          <Metrica
            titulo="Caché de prompt"
            valor={formatearPorcentaje(resumen.data.pct_cacheado)}
            pista="Input servido desde caché — cuanto más alto, más barato"
          />
          <Metrica
            titulo="Rechazadas"
            valor={formatearNumero(resumen.data.rechazadas)}
            pista="Peticiones que no llegaron a despacharse — presupuesto o proveedor"
            alerta={resumen.data.rechazadas > 0}
          />
          <Metrica
            titulo="Medición estimada"
            valor={formatearPorcentaje(resumen.data.pct_estimado)}
            pista="Si sube, hay desconexiones o cortes que investigar"
            alerta={resumen.data.pct_estimado > 10}
          />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Coste diario <span className="font-normal text-muted-foreground">(US$)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {diario.isPending ? (
            <Skeleton className="h-64" />
          ) : diario.isError ? (
            <p className="text-sm text-destructive">No se pudo cargar la serie.</p>
          ) : diario.data.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              Sin consumo en el periodo.
            </p>
          ) : (
            <Suspense fallback={<Skeleton className="h-64" />}>
              <CostChart series={diario.data} />
            </Suspense>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Desglose</CardTitle>
          <Tabs value={por} onValueChange={(v) => setPor(v as Agrupacion)}>
            <TabsList>
              {AGRUPACIONES.map((a) => (
                <TabsTrigger key={a.valor} value={a.valor}>
                  {a.etiqueta}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {agrupado.isPending ? (
            <Skeleton className="h-40" />
          ) : agrupado.isError ? (
            <p className="text-sm text-destructive">No se pudo cargar el desglose.</p>
          ) : agrupado.data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Sin consumo en el periodo.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clave</TableHead>
                    <TableHead className="text-right">Peticiones</TableHead>
                    <TableHead className="text-right">Caché</TableHead>
                    <TableHead className="text-right">Estimado</TableHead>
                    <TableHead className="text-right">Coste</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agrupado.data.map((fila) => (
                    <TableRow key={fila.clave}>
                      <TableCell className="font-medium">{fila.clave}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatearNumero(fila.consumo.peticiones)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatearPorcentaje(fila.consumo.pct_cacheado)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatearPorcentaje(fila.consumo.pct_estimado)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatearCoste(fila.consumo.cost_micros)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
